/**
 * Read-only summary of a submitted sale.
 *
 * Triggered from the eye-icon on each row in the Sales Ledger. Renders every
 * captured field — vehicle, customer, salesperson, on-road price breakdown,
 * exchange / finance blocks, payment tenders, packages, GST, audit trail —
 * in a clean labeled grid with NO inputs. Mirrors what the form captured so
 * staff can quickly recall a deal without re-opening the edit form.
 */
import Drawer from "@/components/Drawer";
import { useState } from "react";
import { fmtMoney, fmtDate, vehicleLabel, exchangeLabel } from "@/lib/format";
import { computeSaleDue, paymentLinesTotal } from "@/lib/saleMath";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Printer, ArrowsLeftRight } from "@phosphor-icons/react";
import { printPaymentReceipt } from "@/lib/paymentReceipt";
import SwapVehicleDialog from "@/components/SwapVehicleDialog";
import AdminEditSaleDialog from "@/components/AdminEditSaleDialog";
import { PencilSimple } from "@phosphor-icons/react";

// Cache dealership settings per-session — reprint can be triggered repeatedly.
let _dealershipCache = null;
async function loadDealership() {
  if (_dealershipCache) return _dealershipCache;
  try {
    const r = await api.get("/settings/dealership");
    _dealershipCache = r.data || {};
  } catch {
    _dealershipCache = {};
  }
  return _dealershipCache;
}

async function reprintPaymentLine(sale, line) {
  if (!line || !line.receipt_no) {
    toast.error("This payment line has no receipt number — cannot reprint.");
    return;
  }
  const dealership = await loadDealership();
  printPaymentReceipt(
    {
      dealership,
      receipt_no: line.receipt_no,
      now: line.paid_on || sale.sale_date || new Date().toISOString(),
      reprint: true,
      reprint_at: new Date().toISOString(),
      line,
      customer: sale.customer || {},
      vehicle: sale.vehicle || {},
      sale: {
        invoice_no: sale.invoice_no || (sale.id ? sale.id.slice(0, 8).toUpperCase() : ""),
        on_road_price: sale.on_road_price,
      },
      // Compute outstanding-after for this specific tender using its position
      // in the payment_lines list — same math as the original first-print.
      outstanding_after: Math.max(
        0,
        Number(sale.due || 0),
      ),
      received_by: sale.salesperson_name || "",
    },
    { format: "thermal", autoPrint: true },
  );
}

function Section({ title, children, testId }) {
  return (
    <section data-testid={testId} className="space-y-2">
      <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted border-b border-canvas-line pb-1.5">
        {title}
      </h3>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">{children}</div>
    </section>
  );
}

function Field({ label, value, mono, testId, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="font-mono text-[9px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div
        data-testid={testId}
        className={`text-ink ${mono ? "font-mono text-xs" : "text-sm"} ${value === "—" ? "text-ink-subtle" : ""}`}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function money(v) {
  return v == null || v === "" ? "—" : fmtMoney(v);
}

export default function SaleDetailDrawer({ open, sale, onClose, onChanged }) {
  const { user } = useAuth();
  const [swapOpen, setSwapOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  if (!open || !sale) return null;

  const due = computeSaleDue(sale);
  const tendered = paymentLinesTotal(sale.payment_lines);
  const packages = sale.packages || [];
  const lines = sale.payment_lines || [];
  // Allow swap only on pre-RTO sales. Admins only; not allowed for rejected
  // or cancelled sales. Registration-in-progress is enforced server-side too.
  const canSwap =
    user?.role === "admin" &&
    sale.vehicle_id &&
    !["rejected", "cancelled"].includes(sale.approval_status || "");
  // Admin-only edit: non-critical fields (payment method, customer attribution,
  // dates, notes). Excluded on rejected/cancelled sales.
  const canAdminEdit =
    user?.role === "admin" &&
    !["rejected", "cancelled"].includes(sale.approval_status || "");

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="Sale · Read-only"
      title={`Sale ${sale.invoice_no || sale.id?.slice(0, 8).toUpperCase()}`}
      testIdClose="sale-detail-close"
    >
      <div className="space-y-6" data-testid="sale-detail-drawer">
        {canAdminEdit && (
          <div className="flex justify-end">
            <button
              onClick={() => setEditOpen(true)}
              data-testid="sale-admin-edit-btn"
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink hover:bg-canvas"
              title="Edit non-critical fields (payment method, dates, attribution, notes)"
            >
              <PencilSimple size={11} weight="bold" />
              Admin Edit
            </button>
          </div>
        )}
        <Section title="Vehicle" testId="sale-detail-vehicle">
          <Field
            label="Vehicle"
            value={vehicleLabel(sale.vehicle)}
            full
          />
          <Field label="VIN" value={sale.vehicle?.vin} mono testId="sale-detail-vin" />
          <Field label="Color" value={sale.vehicle?.color} />
          {canSwap && (
            <div className="col-span-2 mt-1">
              <button
                onClick={() => setSwapOpen(true)}
                data-testid="sale-swap-vehicle-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-canvas-line text-xs hover:border-ink hover:bg-canvas"
              >
                <ArrowsLeftRight size={12} weight="bold" />
                Swap Vehicle (pre-RTO)
              </button>
            </div>
          )}
        </Section>

        <Section title="Customer" testId="sale-detail-customer">
          <Field label="Name" value={sale.customer?.name} testId="sale-detail-cust-name" />
          <Field label="Phone" value={sale.customer?.phone} mono />
          <Field label="Email" value={sale.customer?.email} full mono />
          <Field label="Address" value={sale.customer?.address} full />
        </Section>

        <Section title="Sale info">
          <Field label="Sale date" value={fmtDate(sale.sale_date)} mono />
          <Field label="Sale type" value={(sale.sale_type || "cash").toUpperCase()} mono />
          <Field label="Salesperson" value={sale.salesperson_name} />
          <Field label="Sales Agent" value={sale.sales_agent_name} />
          <Field label="Branch" value={sale.branch_name || sale.branch_id} />
          <Field label="GST type" value={(sale.gst_type || "intra").toUpperCase()} mono />
          <Field label="Model category" value={sale.model_category} mono testId="sale-detail-model-cat" />
          <Field label="Approval status" value={(sale.approval_status || "approved").replace(/_/g, " ").toUpperCase()} mono />
          <Field label="Invoice no." value={sale.invoice_no} mono />
        </Section>

        <Section title="Pricing">
          <Field label="On-road price" value={money(sale.on_road_price)} mono />
          <Field label="Discount" value={money(sale.discount)} mono />
          <Field label="Advance" value={money(sale.advance_amount)} mono />
          <Field label="Sale price" value={money(sale.sale_price)} mono />
          <Field
            label="Tendered total"
            value={money(tendered)}
            mono
            testId="sale-detail-tendered"
          />
          <Field
            label="Due"
            value={due === 0 ? "—" : `${due > 0 ? "" : "−"}${fmtMoney(Math.abs(due))}`}
            mono
            testId="sale-detail-due"
          />
        </Section>

        {/* Proposed disposition — surfaces the write-off / due decision the
            operator captured on the sale form so the verifier can review
            it before approving. Hidden when nothing was proposed. */}
        {(Number(sale.writeoff_amount) > 0 || sale.due_decision_reason) && (
          <Section title="Disposition (needs verifier review)" testId="sale-detail-disposition">
            {Number(sale.writeoff_amount) > 0 && (
              <>
                <Field label="Write-off amount" value={money(sale.writeoff_amount)} mono />
                <Field label="Write-off reason" value={sale.writeoff_reason || "—"} full />
                <Field label="Proposed by" value={sale.writeoff_decided_by || "—"} />
                <Field label="Proposed at" value={sale.writeoff_decided_at ? fmtDate(sale.writeoff_decided_at) : "—"} />
              </>
            )}
            {sale.due_decision_reason && (
              <>
                <Field label="Due reason" value={sale.due_decision_reason} full />
                <Field label="Decided by" value={sale.due_decided_by || "—"} />
                <Field label="Decided at" value={sale.due_decided_at ? fmtDate(sale.due_decided_at) : "—"} />
              </>
            )}
          </Section>
        )}

        <Section title={`Payment tenders (${lines.length})`} testId="sale-detail-tenders">
          {lines.length === 0 ? (
            <div className="col-span-2 text-xs text-ink-subtle">No payment lines captured.</div>
          ) : (
            <div className="col-span-2 border border-canvas-line">
              <table className="w-full text-xs">
                <thead className="bg-canvas-panel">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-ink-muted">Method</th>
                    <th className="text-right px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-ink-muted">Amount</th>
                    <th className="text-left px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-ink-muted">Reference</th>
                    <th className="text-left px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-ink-muted">Paid on</th>
                    <th className="text-left px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-ink-muted">Receipt #</th>
                    <th className="text-center px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-ink-muted">Reprint</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i} className="border-t border-canvas-line">
                      <td className="px-2 py-1.5 font-mono uppercase">{l.method}</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmtMoney(l.amount)}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{l.reference || "—"}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{l.paid_on ? fmtDate(l.paid_on) : "—"}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">
                        {l.receipt_no || "—"}
                        {l._reconciled && (
                          <span className="ml-1 inline-flex px-1 py-0.5 border border-[#E08600] bg-[#FFF6E5] text-[#A35700] text-[8px] font-mono uppercase tracking-wider">
                            RC
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          type="button"
                          onClick={() => reprintPaymentLine(sale, l)}
                          disabled={!l.receipt_no}
                          title={l.receipt_no ? "Reprint receipt" : "No receipt number — cannot reprint"}
                          data-testid={`reprint-payment-line-${i}`}
                          className="p-1 border border-canvas-line hover:border-ink disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center"
                        >
                          <Printer size={11} weight="bold" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {sale.has_exchange && sale.exchange && (
          <Section title="Exchange" testId="sale-detail-exchange">
            <Field label="Description" value={exchangeLabel(sale.exchange)} full />
            <Field label="Year" value={sale.exchange.year} mono />
            <Field label="Reg. no." value={sale.exchange.registration_no} mono />
            <Field label="Valuation" value={money(sale.exchange.valuation)} mono />
            <Field label="Evaluator" value={sale.exchange.evaluator_name} />
          </Section>
        )}

        {sale.has_finance && sale.finance && (
          <Section title="Finance" testId="sale-detail-finance">
            <Field label="Financier" value={sale.finance.financier_name} />
            <Field label="Loan app #" value={sale.finance.application_no} mono />
            <Field label="Finance amount" value={money(sale.finance.finance_amount || sale.finance.amount)} mono />
            <Field label="Down payment" value={money(sale.finance.down_payment)} mono />
            <Field label="Tenure" value={sale.finance.tenure_months ? `${sale.finance.tenure_months} months` : "—"} />
            <Field label="Status" value={sale.finance.status} mono />
          </Section>
        )}

        {packages.length > 0 && (
          <Section title={`Packages / Add-ons (${packages.length})`} testId="sale-detail-packages">
            <div className="col-span-2 border border-canvas-line">
              <table className="w-full text-xs">
                <thead className="bg-canvas-panel">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-ink-muted">Name</th>
                    <th className="text-left px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-ink-muted">Model</th>
                    <th className="text-right px-2 py-1.5 font-mono text-[9px] uppercase tracking-wider text-ink-muted">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((p, i) => (
                    <tr key={i} className="border-t border-canvas-line">
                      <td className="px-2 py-1.5">{p.name || p.label || "—"}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                        {p.model_category || "—"}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmtMoney(p.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {(sale.notes || sale.verification_note || sale.approval_note) && (
          <Section title="Notes" testId="sale-detail-notes">
            <Field label="Notes" value={sale.notes} full />
            <Field label="Verification note" value={sale.verification_note || sale.approval_note} full />
            <Field label="Verified by" value={sale.verified_by_name || sale.approval_changed_by_name} />
            <Field label="Verified at" value={sale.verified_at ? fmtDate(sale.verified_at) : (sale.approval_changed_at ? fmtDate(sale.approval_changed_at) : "—")} />
          </Section>
        )}
      </div>

      <SwapVehicleDialog
        open={swapOpen}
        sourceType="sale"
        sourceId={sale.id}
        currentVehicleId={sale.vehicle_id}
        currentVin={sale.vehicle?.vin}
        onClose={() => setSwapOpen(false)}
        onSwapped={() => {
          setSwapOpen(false);
          onChanged?.();
          onClose?.();
        }}
      />

      {editOpen && (
        <AdminEditSaleDialog
          sale={sale}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            onChanged?.();
          }}
        />
      )}
    </Drawer>
  );
}
